// UnlockCodesPage.tsx — Personal unlock-code admin. Lifted from
// web_marketing/admin.html's `unlock-codes` section. Two cards
// side-by-side: mint form + recent codes list with copy buttons.
//
// Unlock codes are NOT tied to a license_org - they're standalone
// product entitlements typed into the mobile app's "Have an unlock
// code?" tile. Used for refunds / manual entitlement grants.

import { useCallback, useEffect, useState } from 'react';
import { useFirestoreQuery, invalidateCache } from '@/hooks/useFirestoreQuery';
import { mintUnlockCode } from '@/services/functions/mintUnlockCode';
import {
  listRecentUnlockCodes,
  type RecentUnlockCode,
} from '@/services/functions/listRecentUnlockCodes';
import { revokeUnlockCode } from '@/services/functions/revokeUnlockCode';
import {
  listUnlockCodeRedemptions,
  type UnlockCodeRedemption,
} from '@/services/functions/listUnlockCodeRedemptions';
import { SkeletonList } from '@/components/SkeletonRow';
import { ErrorState } from '@/components/ErrorState';
import { Modal } from '@/components/Modal';
import { UserLink } from '@/components/UserLink';

interface Product {
  id: string;
  label: string;
}

// Matches the legacy admin's UNLOCK_PRODUCTS list. Keep in sync
// with the mobile app's `lib/config/revenuecat_config.dart` ids.
const PRODUCTS: Product[] = [
  { id: 'theme_pack', label: 'Theme Pack' },
  { id: 'ringtone_pack', label: 'Ringtone Pack' },
  { id: 'widget_pack', label: 'Widget Pack' },
  { id: 'automations_pack', label: 'Automations Pack' },
  { id: 'ifttt_pack', label: 'IFTTT Pack' },
  { id: 'complete_pack', label: 'Complete Pack' },
];

const RECENT_LIMIT = 20;

export function UnlockCodesPage() {
  const fetcher = useCallback(
    () => listRecentUnlockCodes({ limit: RECENT_LIMIT }),
    [],
  );
  const { data, loading, error, refetch } = useFirestoreQuery(
    'unlockCodes:recent',
    fetcher,
  );

  return (
    <>
      <header className="console-page-header">
        <div>
          <h1 className="console-page-title">Unlock Codes</h1>
          <div className="console-page-subtitle">
            Personal product unlocks. Standalone codes the user types into
            "Have an unlock code?" on the mobile app.
          </div>
        </div>
      </header>

      <div
        style={{
          display: 'grid',
          gap: 16,
          gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
        }}
      >
        <MintCard onMinted={() => {
          invalidateCache('unlockCodes:recent');
          refetch();
        }} />
        <RecentCard
          data={data}
          loading={loading}
          error={error}
          onRefresh={refetch}
        />
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Mint card
// ---------------------------------------------------------------------------

interface MintResult {
  code: string;
  productIds: string[];
  maxUses: number;
  expiresAt?: string;
}

function MintCard({ onMinted }: { onMinted: () => void }) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [maxUses, setMaxUses] = useState(1);
  const [expiresInput, setExpiresInput] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<MintResult | null>(null);

  function toggleProduct(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function handleMint() {
    if (submitting) return;
    setError(null);
    setResult(null);
    if (selected.size === 0) {
      setError('Select at least one product to unlock.');
      return;
    }
    if (!Number.isFinite(maxUses) || maxUses < 1 || maxUses > 1000) {
      setError('Max uses must be a number between 1 and 1000.');
      return;
    }
    let expiresAtIso: string | null = null;
    if (expiresInput) {
      const dt = new Date(expiresInput);
      if (Number.isNaN(dt.getTime())) {
        setError('Invalid expiry date.');
        return;
      }
      if (dt.getTime() < Date.now()) {
        setError('Expiry must be in the future.');
        return;
      }
      expiresAtIso = dt.toISOString();
    }
    setSubmitting(true);
    try {
      const data = await mintUnlockCode({
        productIds: Array.from(selected),
        maxUses,
        expiresAtIso,
        note: note.trim() || null,
      });
      setResult(data);
      setSelected(new Set());
      setMaxUses(1);
      setExpiresInput('');
      setNote('');
      onMinted();
    } catch (err) {
      setError(
        (err as { message?: string })?.message ?? 'Could not mint unlock code',
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="console-card">
      <div className="console-card-title">
        <span className="material-icons" style={{ verticalAlign: 'middle', marginRight: 6 }}>
          add_card
        </span>
        Mint new code
      </div>

      <div className="console-form-field">
        <label className="console-form-label">Products to unlock</label>
        <div className="console-chip-group" style={{ marginTop: 6 }}>
          {PRODUCTS.map((p) => {
            const checked = selected.has(p.id);
            return (
              <button
                key={p.id}
                type="button"
                role="checkbox"
                aria-checked={checked}
                className={`console-chip${checked ? ' is-active' : ''}`}
                onClick={() => toggleProduct(p.id)}
              >
                {p.label}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
        <div style={{ flex: 1 }}>
          <label className="console-form-label" htmlFor="unlock-max-uses">Max uses</label>
          <input
            id="unlock-max-uses"
            className="console-form-input"
            type="number"
            min={1}
            max={1000}
            value={maxUses}
            onChange={(e) => setMaxUses(parseInt(e.target.value, 10) || 1)}
          />
        </div>
        <div style={{ flex: 1 }}>
          <label className="console-form-label" htmlFor="unlock-expires">Expires (optional)</label>
          <input
            id="unlock-expires"
            className="console-form-input"
            type="datetime-local"
            value={expiresInput}
            onChange={(e) => setExpiresInput(e.target.value)}
          />
        </div>
      </div>

      <div className="console-form-field" style={{ marginTop: 16 }}>
        <label className="console-form-label" htmlFor="unlock-note">Note (optional)</label>
        <input
          id="unlock-note"
          className="console-form-input"
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. Support ticket #1234 - Bob's refund"
          maxLength={200}
        />
      </div>

      {error && (
        <div className="console-modal-error" role="alert" style={{ marginTop: 12 }}>
          {error}
        </div>
      )}

      <button
        className="console-btn console-btn-primary"
        type="button"
        onClick={handleMint}
        disabled={submitting}
        style={{ marginTop: 16 }}
      >
        <span className="material-icons">vpn_key</span>
        {submitting ? 'Minting…' : 'Mint code'}
      </button>

      {result && <MintResultPanel result={result} />}
    </div>
  );
}

function MintResultPanel({ result }: { result: MintResult }) {
  const [copied, setCopied] = useState(false);
  function copy() {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(result.code).catch(() => {});
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }
  let expiresLabel = 'no expiry';
  if (result.expiresAt) {
    try {
      expiresLabel = `expires ${new Date(result.expiresAt).toLocaleString()}`;
    } catch {
      // ignore malformed
    }
  }
  return (
    <div className="console-code-reveal" style={{ marginTop: 16 }}>
      <span className="console-code-reveal-label">New code (saved once)</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span
          className="console-code-reveal-value"
          style={{ flex: 1, fontFamily: 'JetBrains Mono, monospace' }}
        >
          {result.code}
        </span>
        <button
          className="console-btn console-btn-ghost"
          type="button"
          onClick={copy}
        >
          <span className="material-icons">
            {copied ? 'check' : 'content_copy'}
          </span>
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <span className="console-code-reveal-warning">
        Save this code now — the plaintext is shown only once. Unlocks:{' '}
        {result.productIds.join(', ')} · max uses: {result.maxUses} · {expiresLabel}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Recent codes list
// ---------------------------------------------------------------------------

// Backend (`listRecentUnlockCodes` in external_checkout.ts) collapses
// the doc's stored status into one of three display values: 'active',
// 'revoked', or 'exhausted'. "Exhausted" covers both max-uses-reached
// AND expired-by-time. We keep an "Expired" filter as a client-side
// overlay - it matches any row whose expiresAt has passed regardless
// of stored status, so admins can see which exhausted codes also lost
// their expiry window.
type StatusFilter = 'all' | 'active' | 'exhausted' | 'expired' | 'revoked';

const STATUS_FILTERS: Array<{ id: StatusFilter; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'active', label: 'Active' },
  { id: 'exhausted', label: 'Exhausted' },
  { id: 'expired', label: 'Expired' },
  { id: 'revoked', label: 'Revoked' },
];

function isExpired(code: RecentUnlockCode): boolean {
  if (!code.expiresAt) return false;
  const t = new Date(code.expiresAt).getTime();
  return Number.isFinite(t) && t < Date.now();
}

function matchesFilter(code: RecentUnlockCode, filter: StatusFilter): boolean {
  if (filter === 'all') return true;
  if (filter === 'expired') return isExpired(code);
  if (filter === 'active') return code.status === 'active' && !isExpired(code);
  return code.status === filter;
}

function RecentCard({
  data,
  loading,
  error,
  onRefresh,
}: {
  data: RecentUnlockCode[] | null;
  loading: boolean;
  error: Error | null;
  onRefresh: () => void;
}) {
  const [filter, setFilter] = useState<StatusFilter>('all');
  const filtered = data?.filter((c) => matchesFilter(c, filter)) ?? null;

  return (
    <div className="console-card">
      <div
        className="console-card-title"
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
      >
        <span>
          <span className="material-icons" style={{ verticalAlign: 'middle', marginRight: 6 }}>
            history
          </span>
          Recent codes
        </span>
        <button
          className="console-btn console-btn-ghost"
          type="button"
          onClick={onRefresh}
          aria-label="Refresh"
          title="Refresh"
        >
          <span className="material-icons">refresh</span>
        </button>
      </div>

      <div className="console-chip-group" style={{ marginTop: 4, marginBottom: 4 }}>
        {STATUS_FILTERS.map((s) => (
          <button
            key={s.id}
            type="button"
            className={`console-chip${filter === s.id ? ' is-active' : ''}`}
            onClick={() => setFilter(s.id)}
          >
            {s.label}
          </button>
        ))}
      </div>

      {loading && !data && <SkeletonList count={4} inset />}
      {error && <ErrorState message={error.message} />}
      {filtered && filtered.length === 0 && (
        <div className="console-empty">
          <span className="material-icons">history</span>
          <div className="console-empty-title">
            {data && data.length === 0 ? 'No codes yet' : 'No matches'}
          </div>
          <div>
            {data && data.length === 0
              ? 'Mint one from the card on the left.'
              : 'Try a different filter.'}
          </div>
        </div>
      )}
      {filtered && filtered.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 12 }}>
          {filtered.map((c) => (
            <RecentRow
              key={`${c.codeHashPrefix}-${c.createdAt}`}
              code={c}
              onRevoked={() => {
                invalidateCache('unlockCodes:recent');
                onRefresh();
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function truncateOwner(ownerId: string): string {
  // Mask the middle of uids / install ids for at-a-glance compactness
  // while keeping the suffix the admin would actually search on. PII
  // visibility is unchanged - this is purely a display trim.
  if (ownerId.length <= 12) return ownerId;
  return `${ownerId.slice(0, 6)}…${ownerId.slice(-4)}`;
}

function RecentRow({
  code,
  onRevoked,
}: {
  code: RecentUnlockCode;
  onRevoked: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [redemptionsOpen, setRedemptionsOpen] = useState(false);
  function copy() {
    if (!code.code || !navigator.clipboard?.writeText) return;
    navigator.clipboard.writeText(code.code).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }
  let createdLabel = '-';
  try {
    createdLabel = new Date(code.createdAt).toLocaleString();
  } catch {
    // ignore malformed
  }
  let expiresLabel = 'no expiry';
  if (code.expiresAt) {
    try {
      expiresLabel = `expires ${new Date(code.expiresAt).toLocaleDateString()}`;
    } catch {
      // ignore malformed
    }
  }
  const expired = isExpired(code);
  let statusClass = 'is-neutral';
  if (code.status === 'active' && !expired) statusClass = 'is-active';
  else if (code.status === 'revoked') statusClass = 'is-suspended';
  else if (code.status === 'expired') statusClass = 'is-expired';
  return (
    <div
      style={{
        padding: 12,
        border: '1px solid var(--border-color)',
        borderRadius: 8,
        background: 'var(--bg-primary)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 6,
          flexWrap: 'wrap',
        }}
      >
        <span style={{ fontSize: 13, color: 'var(--text-primary)' }}>
          {code.productIds.join(', ')}
        </span>
        <span className={`console-status ${statusClass}`}>{code.status}</span>
        {expired && code.status !== 'expired' && (
          <span className="console-status is-expired">expired</span>
        )}
        <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-muted)' }}>
          {createdLabel} · {expiresLabel}
        </span>
      </div>
      {code.code ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <code
            style={{
              flex: 1,
              fontFamily: 'JetBrains Mono, monospace',
              fontSize: 13,
              color: 'var(--text-secondary)',
              wordBreak: 'break-all',
            }}
          >
            {code.code}
          </code>
          <button
            className="console-btn console-btn-ghost"
            type="button"
            onClick={copy}
          >
            <span className="material-icons">
              {copied ? 'check' : 'content_copy'}
            </span>
            {copied ? 'Copied' : 'Copy'}
          </button>
          {code.status !== 'revoked' && (
            <button
              className="console-btn console-btn-ghost"
              type="button"
              onClick={() => setRevokeOpen(true)}
              title={
                code.status === 'exhausted'
                  ? 'Mark as revoked (already unredeemable; closes the code out for audit)'
                  : 'Revoke this code'
              }
            >
              <span className="material-icons">block</span>
              Revoke
            </button>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <code
            style={{
              flex: 1,
              fontFamily: 'JetBrains Mono, monospace',
              fontSize: 12,
              color: 'var(--text-muted)',
            }}
          >
            {code.codeHashPrefix}… · plaintext not stored
          </code>
          {code.status !== 'revoked' && (
            <button
              className="console-btn console-btn-ghost"
              type="button"
              onClick={() => setRevokeOpen(true)}
              title={
                code.status === 'exhausted'
                  ? 'Mark as revoked (already unredeemable; closes the code out for audit)'
                  : 'Revoke this code'
              }
            >
              <span className="material-icons">block</span>
              Revoke
            </button>
          )}
        </div>
      )}
      {code.note && (
        <div
          style={{
            marginTop: 6,
            fontSize: 12,
            color: 'var(--text-muted)',
          }}
        >
          {code.note}
        </div>
      )}
      {code.status === 'revoked' && (code.revokedAt || code.revokeReason) && (
        <div
          style={{
            marginTop: 6,
            fontSize: 12,
            color: 'var(--text-muted)',
          }}
        >
          Revoked
          {code.revokedAt
            ? ` ${new Date(code.revokedAt).toLocaleString()}`
            : ''}
          {code.revokeReason ? ` - ${code.revokeReason}` : ''}
        </div>
      )}
      {code.lastRedeemedAt && code.lastRedeemedBy && (
        <div
          style={{
            marginTop: 6,
            fontSize: 12,
            color: 'var(--text-muted)',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            flexWrap: 'wrap',
          }}
        >
          <span>
            Last redeemed by{' '}
            {code.lastRedeemedByUid ? (
              <UserLink uid={code.lastRedeemedByUid} truncate />
            ) : code.lastRedeemedOwnerKind === 'uid' ? (
              <UserLink uid={code.lastRedeemedBy} truncate />
            ) : (
              <code style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                install:{truncateOwner(code.lastRedeemedBy)}
              </code>
            )}
            {code.lastRedeemedByUid &&
              code.lastRedeemedOwnerKind === 'install' && (
                <>
                  {' '}
                  <span style={{ opacity: 0.7 }}>
                    (install:{truncateOwner(code.lastRedeemedBy)})
                  </span>
                </>
              )}{' '}
            on {new Date(code.lastRedeemedAt).toLocaleString()}
          </span>
          <button
            type="button"
            className="console-btn console-btn-ghost"
            onClick={() => setRedemptionsOpen(true)}
            style={{ padding: '2px 8px', fontSize: 12 }}
          >
            View all ({code.usedCount ?? 0})
          </button>
        </div>
      )}
      {!code.lastRedeemedAt && (code.usedCount ?? 0) > 0 && (
        <div
          style={{
            marginTop: 6,
            fontSize: 12,
            color: 'var(--text-muted)',
            fontStyle: 'italic',
          }}
        >
          {code.usedCount} pre-audit redemption
          {code.usedCount === 1 ? '' : 's'} - history not available
        </div>
      )}
      <RevokeModal
        open={revokeOpen}
        code={code}
        onClose={() => setRevokeOpen(false)}
        onSuccess={() => {
          setRevokeOpen(false);
          onRevoked();
        }}
      />
      <RedemptionsModal
        open={redemptionsOpen}
        code={code}
        onClose={() => setRedemptionsOpen(false)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Revoke modal
// ---------------------------------------------------------------------------

function RevokeModal({
  open,
  code,
  onClose,
  onSuccess,
}: {
  open: boolean;
  code: RecentUnlockCode;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  async function handleConfirm() {
    if (submitting) return;
    setErrorMessage(null);
    setSubmitting(true);
    try {
      await revokeUnlockCode({
        codeHashPrefix: code.codeHashPrefix,
        reason: reason.trim() || undefined,
      });
      setReason('');
      onSuccess();
    } catch (err) {
      setErrorMessage(
        (err as { message?: string })?.message ?? 'Could not revoke this code.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} ariaLabel="Revoke unlock code">
      <div className="console-modal-title">Revoke this unlock code?</div>
      <div className="console-modal-description">
        Future redemptions of this code will fail. Existing entitlements
        already granted by this code are NOT unwound.
      </div>
      <div className="console-modal-meta">
        <div>
          <strong>Products</strong>
          <br />
          {code.productIds.join(', ')}
        </div>
        <div>
          <strong>Prefix</strong>
          <br />
          {code.codeHashPrefix}…
        </div>
      </div>
      <div className="console-form-field">
        <label className="console-form-label" htmlFor="revoke-reason">
          Reason (optional, max 500 chars)
        </label>
        <input
          id="revoke-reason"
          className="console-form-input"
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Refund issued for ticket #1234"
          maxLength={500}
        />
      </div>
      {errorMessage && (
        <div className="console-modal-error" role="alert">
          {errorMessage}
        </div>
      )}
      <div className="console-modal-actions">
        <button
          className="console-btn console-btn-ghost"
          type="button"
          onClick={onClose}
          disabled={submitting}
        >
          Cancel
        </button>
        <button
          className="console-btn console-btn-primary"
          type="button"
          onClick={handleConfirm}
          disabled={submitting}
        >
          {submitting ? 'Revoking…' : 'Revoke code'}
        </button>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Redemptions drill-in modal
// ---------------------------------------------------------------------------

function RedemptionsModal({
  open,
  code,
  onClose,
}: {
  open: boolean;
  code: RecentUnlockCode;
  onClose: () => void;
}) {
  const [redemptions, setRedemptions] = useState<UnlockCodeRedemption[] | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setErrorMessage(null);
    listUnlockCodeRedemptions({ codeHashPrefix: code.codeHashPrefix })
      .then((rows) => {
        if (cancelled) return;
        setRedemptions(rows);
      })
      .catch((err: { message?: string }) => {
        if (cancelled) return;
        setErrorMessage(err?.message ?? 'Could not load redemptions.');
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, code.codeHashPrefix]);

  return (
    <Modal open={open} onClose={onClose} ariaLabel="Unlock code redemptions">
      <div className="console-modal-title">Redemption history</div>
      <div className="console-modal-description">
        Every time this code was redeemed, by whom, and what was granted.
      </div>
      <div className="console-modal-meta">
        <div>
          <strong>Prefix</strong>
          <br />
          {code.codeHashPrefix}…
        </div>
        <div>
          <strong>Used</strong>
          <br />
          {code.usedCount ?? 0} of {code.maxUses ?? '∞'}
        </div>
      </div>
      {loading && <SkeletonList count={3} inset />}
      {errorMessage && (
        <div className="console-modal-error" role="alert">
          {errorMessage}
        </div>
      )}
      {!loading && redemptions && redemptions.length === 0 && (
        <div className="console-empty">
          <span className="material-icons">history</span>
          <div className="console-empty-title">No redemptions yet</div>
          <div>This code has not been used.</div>
        </div>
      )}
      {!loading && redemptions && redemptions.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            marginTop: 12,
            maxHeight: 360,
            overflowY: 'auto',
          }}
        >
          {redemptions.map((r, i) => (
            <div
              key={`${r.redeemedAt}-${i}`}
              style={{
                padding: 10,
                border: '1px solid var(--border-color)',
                borderRadius: 8,
                background: 'var(--bg-primary)',
                fontSize: 12,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  flexWrap: 'wrap',
                  marginBottom: 4,
                }}
              >
                <span className="console-status is-neutral">
                  {r.ownerKind}
                </span>
                {r.ownerKind === 'uid' ? (
                  <UserLink uid={r.ownerId} truncate />
                ) : (
                  <code style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    {r.ownerId}
                  </code>
                )}
                <span style={{ marginLeft: 'auto', color: 'var(--text-muted)' }}>
                  {new Date(r.redeemedAt).toLocaleString()}
                </span>
              </div>
              {r.redeemedByUid && r.redeemedByUid !== r.ownerId && (
                <div
                  style={{
                    color: 'var(--text-muted)',
                    marginBottom: 4,
                    fontSize: 11,
                  }}
                >
                  Signed in as <UserLink uid={r.redeemedByUid} truncate />
                </div>
              )}
              <div style={{ color: 'var(--text-secondary)' }}>
                Granted: {r.productIds.join(', ')}
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="console-modal-actions" style={{ marginTop: 16 }}>
        <button
          className="console-btn console-btn-ghost"
          type="button"
          onClick={onClose}
        >
          Close
        </button>
      </div>
    </Modal>
  );
}
